import { reactive, ref } from '@vue/composition-api';
import Axios from 'axios';
import { Breakpoint, breakpointUp } from '../../../../shared/helper/viewport';
import { publish, subscribe } from '../../../../shared/eventbus/eventbus';
import { Events } from '../../../../shared/eventbus/events';
import { ApiEndpointEnum, getApiRequestConfig } from '../../../config/network';
import { throttle } from '../../../helper/event-control';
import { ConfigurationAPI } from '../../../plugins/configuration/types/configuration';
import { SideNavigation } from './types';
import VueRouter, { Route } from 'vue-router';
import RouteNames from '../../../router/routes';

export const useNavigationData = (
  configApi: ConfigurationAPI,
  routeParams: {
    routeLanguage: string;
    routeTerritory?: string;
  },
) => {
  /**
   * static properties `dashboardUrl` and `gameserverIndexUrl`, they are not changed during runtime
   */
  const getters = {
    dashboardUrl: `/${configApi.routes.localePrefix}/`,
    gameserverIndexUrl: `/${configApi.routes.localePrefix}/gameserver/index`,
  };

  /**
   * @returns reactive properties `navigation` from type `SideNavigation`
   *    and `isLoading` from type `boolean`
   */
  const state = reactive<{ navigation: SideNavigation; isLoading: boolean }>({
    navigation: { items: [] },
    isLoading: true,
  });

  /**
   * returns true if the profile menu should be displayed for the specified route
   * @param route
   */
  const isProfileMenuRoute = (route: Route): boolean => route.matched.some((record) => [
    RouteNames.ProfileRoute,
    RouteNames.ReferrerRoute,
  ].some((r) => r === record.name));

  /**
   * generates the href of our menu
   *
   * @param routeName routenames of the enum
   * @param router the current instance of your router
   * @param paramsOptions if you want to merge the route params
   *    you can specify additional attributes
   */
  const generateMenuRoute = (
    routeName: RouteNames,
    router: VueRouter,
    paramsOptions: Record<string, string> = {},
  ): string => router.resolve({
    name: routeName,
    params: {
      ...paramsOptions,
      regionPrefix: configApi.routes.regionPrefix,
      languagePrefix: routeParams.routeLanguage,
      territoryPrefix: routeParams.routeTerritory,
    },
  }).href;

  /**
   * returns part of the static homepage menu (product menu)
   *
   * @param router
   * @param withEps
   * @param withCreatorBundles
   */
  const getProductMenuData = (
    router: VueRouter,
  ) => {
    const menu = [
      {
        label: 'general:menu.item.game_servers',
        translate: true,
        icon: 'fas fa-gamepad',
        type: 'link',
        data: {
          url: generateMenuRoute(RouteNames.GameserverProductsRoute, router),
        },
      },
      {
        label: 'general:menu.item.voice_server',
        translate: true,
        icon: 'fas fa-microphone',
        type: 'link',
        data: {
          url: configApi.routes.products.voicecloud,
        },
      },
      {
        label: 'general:menu.item.minecraft',
        translate: true,
        icon: 'fas fa-cube',
        type: 'link',
        data: {
          url: configApi.routes.products.minecraft,
        },
      },
    ];

    return menu;
  };

  /**
   * returns part of the homepage menu (community blog)
   * @param router
   */
  const getCommunityMenuData = (
    router: VueRouter,
  ) => ({
    label: 'general:menu.block.community',
    translate: true,
    items: [
      {
        label: 'general:menu.item.news',
        translate: true,
        icon: 'far fa-newspaper',
        type: 'link',
        data: {
          url: generateMenuRoute(RouteNames.NewsOverviewRoute, router),
        },
      },
      {
        label: 'general:menu.item.wiki',
        translate: true,
        icon: 'fas fa-book-open',
        type: 'external',
        data: {
          url: process.env.VUE_APP_MENU_SEARCH_WIKI, // '/wiki/en',
        },
      },
      {
        label: 'general:menu.item.forum',
        translate: true,
        icon: 'fas fa-list',
        type: 'external',
        data: {
          url: 'https://forum.g-portal.com',
        },
      },
    ],
  });

  /**
   * returns the static menu for the profile
   * @param router
   */
  const getProfileMenu = (
    router: VueRouter,
  ): SideNavigation => {
    const menu = {
      uplink: {
        label: 'general:menu.back-to-home',
        translate: true,
        data: {
          url: generateMenuRoute(RouteNames.HomepageRoute, router),
        },
      },
      items: [{
        label: 'general:menu.block.settings',
        translate: true,
        items: [
          {
            label: 'general:menu.item.your_settings',
            translate: true,
            icon: 'fas fa-wrench',
            type: 'link',
            data: {
              url: generateMenuRoute(RouteNames.ProfileRoute, router),
            },
          }, {
            label: 'general:menu.item.payments',
            translate: true,
            icon: 'fas fa-credit-card',
            type: 'link',
            data: {
              url: generateMenuRoute(RouteNames.ProfilePaymentTransactionRoute, router),
            },
          }],
      }],
    };

    if (configApi.customer && configApi.customer.is_referrer) {
      menu.items.find((item) => item.label === 'general:menu.block.settings').items.push({
        label: 'general:menu.item.referrer',
        translate: true,
        icon: 'fas fa-cart-arrow-down',
        type: 'link',
        data: {
          url: generateMenuRoute(RouteNames.ReferrerRoute, router), // --- "/eur/referrer"
        },
      });
    }

    return menu;
  };

  /**
   * returns the content of our static menu (first level)
   *
   * @param router
   * @param isAuthenticated boolean
   * @returns the static side navigation
   */
  const getFirstLevelMenu = (
    router: VueRouter,
    isAuthenticated: boolean,
  ): SideNavigation => (isAuthenticated ? {
    uplink: null,
    items: [
      {
        label: 'general:menu.block.account',
        translate: true,
        items: [
          {
            label: 'general:menu.item.my_servers',
            translate: true,
            icon: 'fas fa-server',
            type: 'link',
            data: {
              url: generateMenuRoute(RouteNames.GamecloudDashboardRoute, router),
            },
          },
          {
            label: 'general:menu.item.settings',
            translate: true,
            icon: 'far fa-user',
            type: 'link',
            data: {
              url: generateMenuRoute(RouteNames.ProfileRoute, router),
            },
          },
          {
            label: 'general:menu.item.recharge_credit',
            translate: true,
            icon: 'far fa-credit-card',
            type: 'modal',
            data: {
              modal: 'recharge',
            },
          },
          {
            label: 'general:menu.item.support',
            translate: true,
            icon: 'fas fa-headset',
            type: 'link',
            data: {
              url: generateMenuRoute(RouteNames.SupportRoute, router),
            },
          },
        ],
      },
      {
        label: 'general:menu.block.products',
        translate: true,
        items: getProductMenuData(router),
      },
      getCommunityMenuData(router),
    ],
  } : /* NOT LOGGED IN */ {
    uplink: null,
    items: [
      {
        label: 'general:menu.block.products',
        translate: true,
        items: getProductMenuData(router),
      },
      getCommunityMenuData(router),
      {
        label: 'general:menu.block.business',
        translate: true,
        items: [
          {
            label: 'general:menu.item.game_studios',
            translate: true,
            icon: 'far fa-building',
            type: 'external',
            data: {
              url: process.env.VUE_APP_MENU_BUSINESS_BLOG_URL,
            },
          },
          {
            label: 'general:menu.item.case_studies',
            translate: true,
            icon: 'fas fa-bullhorn',
            type: 'external',
            data: {
              url: process.env.VUE_APP_MENU_BUSINESS_CASE_STUDIES_URL,
            },
          },
        ],
      },
    ],
  });

  const getServerIdFromRoute = (route: Route): number => {
    if ('serverId' in route.params && route.params.serverId != null) {
      return parseInt(route.params.serverId);
    }

    return -1;
  };
  /**
   * @returns path to fetch the correct menu structure for the correct context
   */
  const getSubPath = (router: VueRouter) => {
    const { currentRoute } = router;
    const records = currentRoute.matched;

    if (records.some((record) => record.name === RouteNames.GamecloudDashboardRoute)) {
      return '/clouds';
    }
    if (records.some((record) => record.name === RouteNames.ServerRoute)) {
      const serverId = getServerIdFromRoute(currentRoute);

      if (serverId === -1) {
        throw new Error('Server id could not be detected!');
      }

      if ('productKey' in currentRoute.params) {
        switch (currentRoute.params.productKey) {
          case 'atlas':
            return `/server/n${serverId}`;

          case 'teamspeak':
            return `/server/v${serverId}`;

          default:
            return `/server/g${serverId}`;
        }
      }
    }

    return undefined;
  };

  /**
   * fetches the menu structure and sets `state.navigation` property
   * @param subpath used for the ajax request to fetch the menu
   */
  const fetchNavigation = async (subpath: string | undefined) => {
    const { data } = await Axios.get<SideNavigation>(
      subpath,
      getApiRequestConfig(ApiEndpointEnum.Navigation),
    );
    state.navigation = data;
  };

  return {
    state,
    getters,
    fetchNavigation,
    getSubPath,
    getFirstLevelMenu,
    getProfileMenu,
    isProfileMenuRoute,
  };
};

const NAVIGATION_OPEN_NAVIGATION_SELECTOR = '[data-show-navigation]';

export const useMobileBehaviour = () => {
  const bodyStyleClassOnOpen = 'base-app-modal-open';
  const isShowingOnMobile = ref<boolean>(false);
  const isShowingBackdrop = ref<boolean>(false);

  const toggleMobileMenu = (newState: boolean) => {
    isShowingOnMobile.value = newState;
    isShowingBackdrop.value = newState;
  };

  const showMobileMenu = () => {
    toggleMobileMenu(true);
    document.body.classList.add(bodyStyleClassOnOpen);
  };

  const hideMobileMenu = () => {
    toggleMobileMenu(false);
    document.body.classList.remove(bodyStyleClassOnOpen);
  };

  const windowResizeListener = throttle(() => {
    if (breakpointUp(Breakpoint.xlarge)) {
      toggleMobileMenu(false);
    }
  }, 200, false);

  const initMobileEvents = () => {
    // find open menu btn
    const openMenuBtn = document.querySelector(NAVIGATION_OPEN_NAVIGATION_SELECTOR);
    if (openMenuBtn != null) {
      openMenuBtn.addEventListener('click', showMobileMenu);
    }

    // ensure that when screen size is desktop size -> reset mobile state
    window.addEventListener('resize', windowResizeListener);
  };

  const removeMobileEvents = () => {
    const openMenuBtn = document.querySelector(NAVIGATION_OPEN_NAVIGATION_SELECTOR);
    if (openMenuBtn != null) {
      openMenuBtn.removeEventListener('click', showMobileMenu);
    }

    window.removeEventListener('resize', windowResizeListener);
  };

  return {
    showMobileMenu,
    hideMobileMenu,
    isShowingOnMobile,
    isShowingBackdrop,
    initMobileEvents,
    removeMobileEvents,
  };
};

export const useSearch = ({
  onSearchOpen,
  onSearchHide,
}: { onSearchOpen: () => void; onSearchHide: () => void }) => {
  /**
   * dispatches a global event which triggers the search overlay to open.
   * **Warning:** VERY HACKY AND NOT CLEAN!
   */
  const openSearch = () => {
    publish(Events.OpenSearchOverlay, {});
    onSearchOpen();
  };

  const { unsubscribe } = subscribe(Events.HideSearchOverlay, onSearchHide);

  const removeSearchEvents = () => {
    unsubscribe();
  };

  return {
    openSearch,
    removeSearchEvents,
  };
};
